import React, { useState } from 'react';
import {
  Save,
  Paperclip,
  Eye,
  Code,
  Loader2,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import emailTemplatesApi, { EmailTemplate, EmailTemplateAttachment } from '../../services/email-templates-api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';

const SYSTEM_VARIABLES = [
  { name: 'contact.name', description: "Contact's full name" },
  { name: 'contact.first_name', description: "Contact's first name" },
  { name: 'contact.last_name', description: "Contact's last name" },
  { name: 'contact.email', description: "Contact's email" },
  { name: 'contact.phone', description: "Contact's phone" },
  { name: 'contact.company', description: "Contact's company" },
  { name: 'contact.city', description: "Contact's city" },
  { name: 'contact.country', description: "Contact's country" },
  { name: 'account.name', description: 'Your company name' },
  { name: 'agent.name', description: 'Sending agent name' },
  { name: 'agent.email', description: 'Sending agent email' },
  { name: 'current_date', description: 'Current date' },
  { name: 'current_time', description: 'Current time' },
];

const CATEGORIES = [
  { value: 'marketing', label: 'Marketing' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'support', label: 'Support' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'notification', label: 'Notification' },
  { value: 'other', label: 'Other' },
];

interface Props {
  template?: EmailTemplate | null;
  onSave: () => void;
  onCancel: () => void;
  /** When true, omit page-level title (parent Sheet provides header). */
  embedded?: boolean;
}

const EmailTemplateBuilder: React.FC<Props> = ({
  template,
  onSave,
  onCancel,
  embedded = false,
}) => {
  const [activeTab, setActiveTab] = useState<'html' | 'text'>('html');
  const [name, setName] = useState(template?.name || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [bodyHtml, setBodyHtml] = useState(template?.body_html || '');
  const [bodyText, setBodyText] = useState(template?.body_text || '');
  const [category, setCategory] = useState(template?.category || '');
  const [signatureEnabled, setSignatureEnabled] = useState(template?.signature_enabled ?? true);
  const [customSignature, setCustomSignature] = useState(template?.custom_signature || '');
  const [customVariables, setCustomVariables] = useState<string[]>(
    (template?.variables || []).filter((v) => !SYSTEM_VARIABLES.find((sv) => sv.name === v)),
  );
  const [attachments, setAttachments] = useState<EmailTemplateAttachment[]>(template?.attachments || []);
  const [newVariable, setNewVariable] = useState('');
  const [isActive, setIsActive] = useState(template?.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);

  const allVariables = [...SYSTEM_VARIABLES.map((v) => v.name), ...customVariables];

  const handleAddVariable = () => {
    const trimmed = newVariable.trim().toLowerCase().replace(/\s+/g, '_');
    if (trimmed && !allVariables.includes(trimmed)) {
      setCustomVariables([...customVariables, trimmed]);
      setNewVariable('');
    }
  };

  const handleRemoveVariable = (variable: string) => {
    setCustomVariables(customVariables.filter((v) => v !== variable));
  };

  const insertVariable = (variable: string) => {
    const placeholder = `{{${variable}}}`;
    if (activeTab === 'html') setBodyHtml(bodyHtml + placeholder);
    else setBodyText(bodyText + placeholder);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const newAttachment: EmailTemplateAttachment = {
      filename: file.name,
      file_url: URL.createObjectURL(file),
      file_size: file.size,
      mime_type: file.type,
      is_inline: false,
    };
    setAttachments([...attachments, newAttachment]);
    event.target.value = '';
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!name.trim() || !subject.trim() || !bodyHtml.trim()) {
      setError('Name, subject, and body are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data = {
        name: name.trim(),
        subject: subject.trim(),
        body_html: bodyHtml,
        body_text: bodyText || undefined,
        category: category || undefined,
        signature_enabled: signatureEnabled,
        custom_signature: customSignature || undefined,
        variables: [...SYSTEM_VARIABLES.map((v) => v.name), ...customVariables],
        attachments,
        is_active: isActive,
      };
      if (template) await emailTemplatesApi.update(template.id, data);
      else await emailTemplatesApi.create(data);
      onSave();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const renderPreview = () => {
    let previewHtml = bodyHtml;
    let previewSubject = subject;
    const sampleData: Record<string, string> = {
      'contact.name': 'John Doe',
      'contact.first_name': 'John',
      'contact.last_name': 'Doe',
      'contact.email': 'john@example.com',
      'contact.phone': '+1234567890',
      'contact.company': 'Acme Inc',
      'contact.city': 'New York',
      'contact.country': 'USA',
      'account.name': 'Your Company',
      'agent.name': 'Support Agent',
      'agent.email': 'agent@company.com',
      'current_date': new Date().toLocaleDateString(),
      'current_time': new Date().toLocaleTimeString(),
    };
    allVariables.forEach((v) => {
      const value = sampleData[v] || `[${v}]`;
      previewHtml = previewHtml.replace(new RegExp(`\\{\\{${v}\\}\\}`, 'g'), value);
      previewSubject = previewSubject.replace(new RegExp(`\\{\\{${v}\\}\\}`, 'g'), value);
    });
    return (
      <Card className="border-border/60 bg-muted/20 shadow-none">
        <CardContent className="space-y-4 p-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Subject</p>
            <p className="text-lg font-semibold tracking-tight">{previewSubject}</p>
          </div>
          <Separator />
          <div className="prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          {signatureEnabled && customSignature && (
            <>
              <Separator />
              <div className="prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: customSignature }} />
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', embedded ? '' : 'gap-4 p-4 md:p-6')}>
      {!embedded && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
          <h2 className="text-xl font-semibold tracking-tight">
            {template ? 'Edit Email Template' : 'Create Email Template'}
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant={previewMode ? 'default' : 'outline'}
              size="sm"
              className="gap-1.5"
              onClick={() => setPreviewMode(!previewMode)}
            >
              <Eye className="h-3.5 w-3.5" />
              {previewMode ? 'Edit' : 'Preview'}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" size="sm" className="gap-1.5" onClick={() => void handleSave()} disabled={saving}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {saving ? 'Saving…' : 'Save template'}
            </Button>
          </div>
        </div>
      )}

      {embedded && (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-b border-border px-6 py-3.5">
          <Button
            type="button"
            variant={previewMode ? 'default' : 'outline'}
            size="sm"
            className="gap-1.5"
            onClick={() => setPreviewMode(!previewMode)}
          >
            <Eye className="h-3.5 w-3.5" />
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" size="sm" className="gap-1.5" onClick={() => void handleSave()} disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {saving ? 'Saving…' : 'Save template'}
          </Button>
        </div>
      )}

      {error && (
        <Alert variant="destructive" className={cn(embedded ? 'mx-6 mt-4' : 'mt-2')}>
          <AlertDescription className="flex items-center justify-between gap-2">
            <span>{error}</span>
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setError(null)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <ScrollArea className={cn('min-h-0 flex-1', embedded && 'px-6')}>
        <div className={cn('space-y-6 pb-8', embedded ? 'pt-5' : 'pt-2')}>
          {previewMode ? (
            renderPreview()
          ) : (
            <div className="grid gap-6 lg:grid-cols-12">
              <div className="space-y-6 lg:col-span-8">
                <Card className="shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Content</CardTitle>
                    <CardDescription>Name, category, subject, and body</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2 sm:col-span-1">
                        <Label htmlFor="tmpl-name">Template name</Label>
                        <Input
                          id="tmpl-name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="e.g. welcome_email"
                        />
                      </div>
                      <div className="space-y-2 sm:col-span-1">
                        <Label>Category</Label>
                        <Select value={category || '__none__'} onValueChange={(v) => setCategory(v === '__none__' ? '' : v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            {CATEGORIES.map((cat) => (
                              <SelectItem key={cat.value} value={cat.value}>
                                {cat.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tmpl-subject">Email subject</Label>
                      <Input
                        id="tmpl-subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="e.g. Welcome {{contact.first_name}}!"
                      />
                      <p className="text-xs text-muted-foreground">Use {'{{'}variable_name{'}}'} for dynamic content</p>
                    </div>
                    <div className="space-y-2">
                      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'html' | 'text')}>
                        <TabsList className="grid w-full max-w-md grid-cols-2">
                          <TabsTrigger value="html" className="gap-1.5 text-xs">
                            <Code className="h-3.5 w-3.5" /> HTML body
                          </TabsTrigger>
                          <TabsTrigger value="text" className="text-xs">
                            Plain text
                          </TabsTrigger>
                        </TabsList>
                        <TabsContent value="html" className="mt-3 space-y-2">
                          <Textarea
                            value={bodyHtml}
                            onChange={(e) => setBodyHtml(e.target.value)}
                            rows={14}
                            className="min-h-[220px] font-mono text-xs"
                            placeholder="<h1>Hello {{contact.first_name}}</h1>…"
                          />
                        </TabsContent>
                        <TabsContent value="text" className="mt-3 space-y-2">
                          <Textarea
                            value={bodyText}
                            onChange={(e) => setBodyText(e.target.value)}
                            rows={14}
                            className="min-h-[220px] text-sm"
                            placeholder="Plain text version…"
                          />
                        </TabsContent>
                      </Tabs>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-sm">
                  <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
                    <div>
                      <CardTitle className="text-base">Email signature</CardTitle>
                      <CardDescription>Appended when signature is enabled for sends</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch id="sig-en" checked={signatureEnabled} onCheckedChange={setSignatureEnabled} />
                      <Label htmlFor="sig-en" className="text-sm font-normal">
                        Include
                      </Label>
                    </div>
                  </CardHeader>
                  {signatureEnabled && (
                    <CardContent>
                      <Label htmlFor="sig-html" className="sr-only">
                        Custom signature HTML
                      </Label>
                      <Textarea
                        id="sig-html"
                        value={customSignature}
                        onChange={(e) => setCustomSignature(e.target.value)}
                        rows={4}
                        placeholder="<p>Best regards,<br>{{agent.name}}</p>"
                        className="font-mono text-xs"
                      />
                    </CardContent>
                  )}
                </Card>

                <Card className="shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                    <div>
                      <CardTitle className="text-base">Attachments</CardTitle>
                      <CardDescription>Optional files sent with this template</CardDescription>
                    </div>
                    <Button type="button" variant="outline" size="sm" className="gap-1.5" asChild>
                      <label className="cursor-pointer">
                        <Paperclip className="h-3.5 w-3.5" />
                        Add attachment
                        <input type="file" className="hidden" onChange={handleFileUpload} />
                      </label>
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {attachments.length > 0 ? (
                      <ul className="divide-y divide-border rounded-md border border-border">
                        {attachments.map((att, index) => (
                          <li key={index} className="flex items-center gap-3 px-3 py-2 text-sm">
                            <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-medium">{att.filename}</p>
                              {att.file_size != null && (
                                <p className="text-xs text-muted-foreground">
                                  {(att.file_size / 1024).toFixed(1)} KB
                                </p>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                              onClick={() => handleRemoveAttachment(index)}
                              aria-label="Remove attachment"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">No attachments yet.</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6 lg:col-span-4">
                <Card className="shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Variables</CardTitle>
                    <CardDescription>Click a chip to insert at cursor (HTML tab) or into subject (text tab)</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div>
                      <p className="mb-2 text-xs font-medium text-muted-foreground">System</p>
                      <TooltipProvider delayDuration={200}>
                        <div className="flex flex-wrap gap-1.5">
                          {SYSTEM_VARIABLES.map((v) => (
                            <Tooltip key={v.name}>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="secondary"
                                  className="cursor-pointer px-2 py-0.5 font-mono text-[10px]"
                                  onClick={() => insertVariable(v.name)}
                                >
                                  {v.name}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-xs text-xs">
                                {v.description}
                              </TooltipContent>
                            </Tooltip>
                          ))}
                        </div>
                      </TooltipProvider>
                    </div>
                    <Separator />
                    <div>
                      <p className="mb-2 text-xs font-medium text-muted-foreground">Custom</p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="variable_name"
                          value={newVariable}
                          onChange={(e) => setNewVariable(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddVariable()}
                          className="font-mono text-xs"
                        />
                        <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={handleAddVariable}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {customVariables.map((v) => (
                          <Badge
                            key={v}
                            variant="outline"
                            className="cursor-pointer gap-1 pl-2 pr-1 font-mono text-[10px]"
                            onClick={() => insertVariable(v)}
                          >
                            {v}
                            <button
                              type="button"
                              className="rounded-sm p-0.5 hover:bg-muted"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveVariable(v);
                              }}
                              aria-label={`Remove ${v}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Settings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
                      <div>
                        <p className="text-sm font-medium">Active</p>
                        <p className="text-xs text-muted-foreground">Inactive templates cannot be used for sending</p>
                      </div>
                      <Switch checked={isActive} onCheckedChange={setIsActive} />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default EmailTemplateBuilder;
